import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { SearchRequest } from 'src/app/shared/models/general/search-request';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import { TransactionSearchRequest } from 'src/app/shared/models/transactions/transaction-search-request';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import * as XLSX from 'xlsx';
import { formatDate } from '@angular/common';
import { TransactionStatus } from 'src/app/shared/models/transactions/transaction-status';
import { LookupService } from 'src/app/services/lookup.service';
@Component({
  selector: 'app-user-trades',
  templateUrl: './user-trades.component.html',
  styleUrls: ['./user-trades.component.scss'],
})
export class UserTradesComponent implements OnInit, OnChanges {
  @ViewChild('userTradeHistory', { static: true }) userTradeHistory: any;
  @Input() selectedUserProfile: UserProfile;
  @Input() showModal: boolean = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  constructor(
    public transactionsService: TransactionService,
    public settingsService: SettingsService,
    private lookupService: LookupService
  ) {}

  //General
  busy: boolean = true;
  showError: boolean = false;
  errorMsg = '';

  userProfile: UserProfile = new UserProfile();
  transactionStatuses: TransactionStatus[] = [];
  //Trade transactions
  searchRequest: SearchRequest = new SearchRequest();
  transactions: Transaction[] = [];
  selectedTransaction: Transaction = new Transaction();
  averageTradeProfitZar: number = 0;
  totalProfit: number = 0;
  averageProfitPercentage: number = 0;
  totalFIAUsed: number = 0;
  totalSDAUsed: number = 0;
  totalTraded: number = 0;

  public transactionSearchRequest: TransactionSearchRequest = new TransactionSearchRequest();
  public brokerId: number;

  DateFilter: { Id: number; Name: string }[] = [
    { Id: 1, Name: 'Current Calendar Year' },
    { Id: 2, Name: 'Current Tax Year' },
    { Id: 3, Name: 'Previous Calendar Year' },
    { Id: 4, Name: 'Previous Tax Year' },
    { Id: 5, Name: 'All' },
  ];

  ngOnInit(): void {
    this.GetTransactionStatuses();
  }

  ngOnChanges() {
    this.showHideModal(null);
  }

  onClosed(event: any) {
    if (this.showModal != false) {
      this.showModal = false;
      this.showModalChange.emit(this.showModal);
    }
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }

    if (this.showModal) {
      this.userTradeHistory.show();
      this.userProfile = <UserProfile>JSON.parse(JSON.stringify(this.selectedUserProfile));
      this.SetupUserTradingSearchRequest();
      this.GetTradeHistory();
    } else if (!this.showModal) {
      // Blur any focused element before hiding modal to prevent aria-hidden warning
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.showError = false;
      this.errorMsg = '';
      this.transactionSearchRequest = null;
      this.userTradeHistory.hide();
    }

    this.showModalChange.emit(this.showModal);
  }

  GetTradeHistory() {
    this.busy = true;
    this.transactionsService.SearchTransactions(this.transactionSearchRequest).subscribe(
      (response) => {
        this.transactions = response;
        this.CalculateTotals();
        this.busy = false;
        this.showError = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = true;
      }
    );
  }

  SetupUserTradingSearchRequest() {
    this.transactionSearchRequest = new TransactionSearchRequest();
    this.transactionSearchRequest.transactionStatusId = -999; //-999 uses the default API filter which shows ALL
    this.transactionSearchRequest.userId = this.selectedUserProfile.userId;
    this.transactionSearchRequest.dateFilter = this.DateFilter[0].Id; // Current Calendar Year
    this.transactionSearchRequest.brokerId = this.settingsService.brokerUsers[0].brokerId;
    this.transactionSearchRequest.includeDocuments = false;
  }

  CalculateTotals() {
    this.totalProfit = 0;
    this.averageProfitPercentage = 0;
    this.totalFIAUsed = 0;
    this.totalSDAUsed = 0;
    this.totalTraded = 0;
    this.averageTradeProfitZar = 0;

    if (this.transactions.length > 0) {
      let totalProfitPercentage: number = 0;

      this.transactions.forEach((x) => {
        this.totalProfit += x.totalProfit;
        totalProfitPercentage += x.totalProfitPercentage;
        this.totalFIAUsed += x.fiaAmount;
        this.totalSDAUsed += x.sdaAmount;
        this.totalTraded += x.amountSent;
      });

      this.averageProfitPercentage = totalProfitPercentage / this.transactions.length;
      this.averageTradeProfitZar = this.totalProfit / this.transactions.length;
    }
  }

  GetTransactionStatuses() {
    this.lookupService.GetTransactionStatuses().subscribe({
      next: (statusList: TransactionStatus[]) => {
        this.transactionStatuses = statusList;
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      },
    });
  }

  GetStatusDisplayName(statusId: number) {
    const status = this.FindTransactionStatusInListById(statusId);
    return status.name;
  }

  GetHoverHelpText(statusId: number) {
    const status = this.FindTransactionStatusInListById(statusId);
    return status.helpText;
  }

  FindTransactionStatusInListById(statusId: number) {
    const status = this.transactionStatuses.find((x) => x.id === statusId);
    if (!status) {
      let s = new TransactionStatus();
      s.id = statusId;
      s.name = 'Unknown Status';
      return s;
    }
    return status;
  }

  toggleFiaPinsTable(trans: Transaction) {
    if (trans.selected) {
      trans.selected = false;
    } else {
      trans.selected = true;
    }
  }

  Extract(idselector: string) {
    const filename =
      this.userProfile.fullName.replace(' ', '') +
      '_' +
      this.DateFilter[this.transactionSearchRequest.dateFilter - 1].Name.replace(' ', '') +
      '_TradeExport_';

    // Map the transaction data to a clean format for export
    const exportData = this.transactions.map((trans) => {
      return {
        '': '',
        Profit: trans.totalProfit,
        'Profit %': trans.totalProfitPercentage,
        'Trade Amount': trans.amountSent,
        'Start Date': formatDate(trans.transactionStartDate, 'yyyy-MM-dd', 'en'),
        'Completion Date': formatDate(trans.transactionEndDate, 'yyyy-MM-dd', 'en'),
        Status: this.GetStatusDisplayName(trans.transactionStatus.id),
        'FIA Amount': trans.fiaAmount,
        'SDA Amount': trans.sdaAmount,
      };
    });

    // blank row
    const blankRow = {
      '': '',
      Profit: null,
      'Profit %': null,
      'Trade Amount': null,
      'Start Date': '',
      'Completion Date': '',
      Status: '',
      'FIA Amount': null,
      'SDA Amount': null,
    };

    exportData.push(blankRow);

    // Create the total row object
    const totalRow = {
      '': 'Totals:',
      Profit: this.totalProfit,
      'Profit %': null,
      'Trade Amount': this.totalTraded,
      'Start Date': '',
      'Completion Date': '',
      Status: '',
      'FIA Amount': this.totalFIAUsed,
      'SDA Amount': this.totalSDAUsed,
    };

    // Add the total row to the export data
    exportData.push(totalRow);

    // Generate the worksheet from the JSON data
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

    // Apply number formatting to the desired columns
    const range = XLSX.utils.decode_range(ws['!ref']);

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      // R+1 to skip header row
      const profitCell = ws[XLSX.utils.encode_cell({ r: R, c: 1 })];
      if (profitCell) profitCell.z = '#,##0.00';

      const profitPercentCell = ws[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (profitPercentCell) profitPercentCell.z = '0.00';

      const tradeAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 3 })];
      if (tradeAmountCell) tradeAmountCell.z = '#,##0';

      const fiaAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 7 })];
      if (fiaAmountCell) fiaAmountCell.z = '#,##0';

      const sdaAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 8 })];
      if (sdaAmountCell) sdaAmountCell.z = '#,##0';
    }

    // Generate workbook and save the file
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename + formatDate(new Date(), 'yyyyMMdd', 'en') + '.xlsx');
  }
}
